import {
  Card,
  Divider,
  Switch,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import type { FeatureFlags } from '../../../types';
import { removeNonNumericCharacters } from '../../../utils';
import { TextInput } from '../BasicDetails/TextInput';
import OnOffTypography from '../OnOffTypography';
import SetupGridItem from '../SetupGridItem';
import { CollectionsSetupSection } from './CollectionsSetup';
import ConserviceSetup from './ConserviceSetup';
import { useBlueMoonRow, useFetchLatestChangeHistoryLog } from './hooks';
import LegacyScreeningsSetup from './LegacyScreeningsSetup';
import messages from './messages';
import MunibillingSetup from './MunibillingSetup';
import ScreeningsSetup from './ScreeningsSetup';
import TheWorkNumberSetup from './TheWorkNumberSetup';
import { getWorkNumberIntegrationFromList } from './utils';

type Props = {
  propertyDetails: Object,
  editMode: boolean,
  onChange: Function,
  disabled: boolean,
  organization: Object,
  isLoading: boolean,
};

const IntegrationsSetup = (props: Props) => {
  const {
    organizationId,
    propertyId,
    propertyDetails,
    editMode: editModeProp,
    disabled,
    onChange,
    organization,
    intl,
    isLoading,
  } = props;
  const {
    isDomusoActive,
    domusoPropertyCode,
    transunionPropertyId,
    isTransUnionActive,
    propertyIntegrations,
    propertyClass,
    screeningSetupData,
    revenueManagementProviderName,
  } = propertyDetails;

  const { integrations: organizationIntegrations } = organization;

  const {
    theWorkNumber,
    collectionsOneWay,
    transunionOffLeaseAppOff,
    transunionCodeSave,
    rebaRevMgmt,
    blueMoonLeaseSigning,
  }: FeatureFlags = useFlags();

  const { status: blueMoonStatus, isLoading: isBlueMoonLoading } =
    useBlueMoonRow(organizationId, propertyId, blueMoonLeaseSigning);

  const {
    data: transUnionLatestChangeHistoryLog,
    isLoading: transUnionIsLoading,
    refetch: transUnionRefetch,
  } = useFetchLatestChangeHistoryLog(
    organizationId,
    propertyId,
    'isTransUnionActive',
    transunionOffLeaseAppOff,
  );

  const {
    data: transUnionCodeChangeHistoryLog,
    refetch: transUnionCodeRefetch,
    remove: transUnionCodeRemove,
  } = useFetchLatestChangeHistoryLog(
    organizationId,
    propertyId,
    'transunionPropertyId',
    transunionCodeSave,
  );

  useEffect(() => {
    if (transunionOffLeaseAppOff) {
      transUnionRefetch();
    }
    if (transunionCodeSave) {
      if (!transUnionCodeChangeHistoryLog) {
        transUnionCodeRemove();
      }
      transUnionCodeRefetch();
    }
    // eslint-disable-next-line
  }, [isTransUnionActive]);

  useEffect(() => {
    if (
      transunionCodeSave &&
      !isLoading &&
      !transunionPropertyId &&
      isTransUnionActive &&
      transUnionCodeChangeHistoryLog
    ) {
      onChange({
        target: {
          name: 'transunionPropertyId',
          value:
            transUnionCodeChangeHistoryLog.changeFromValue !== 'NULL'
              ? transUnionCodeChangeHistoryLog.changeFromValue
              : transUnionCodeChangeHistoryLog.changeToValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isTransUnionActive, transUnionCodeChangeHistoryLog]);

  // Parse the work number integrations from propertyDetails
  const propertyWorkNumberIntegration =
    getWorkNumberIntegrationFromList(propertyIntegrations);

  // Parse the work number integrations from organization
  const orgWorkNumberIntegration = getWorkNumberIntegrationFromList(
    organizationIntegrations,
  );

  const doesOrgHaveWorkNumberIntegration = !!orgWorkNumberIntegration;
  const isAffordableOrMixedProperty =
    propertyClass === 'Affordable' || propertyClass === 'Mixed';

  const editMode = editModeProp && !disabled;

  // This function helps to convert the new Switch data to match parent form utility function
  const handleSwitchChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.checked;
    onChange({ target: { name, value } });
  };
  const handleRadioGroupChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    onChange({ target: { name, value } });
  };

  const revenueManagementProviderDisplayName =
    revenueManagementProviderName ??
    intl.formatMessage(messages.revenueManagementProviderNone);

  const shouldShowNewScreeningSetup =
    !!screeningSetupData && Object.keys(screeningSetupData).length > 0;

  return (
    <Card sx={{ padding: 2, height: '100%' }}>
      <Typography variant={'titleLarge'}>
        <FormattedMessage {...messages.integrationsSetup} />
      </Typography>

      <Divider sx={{ marginY: 2 }} />

      <Grid container spacing={2}>
        {/* GENERAL */}
        <Grid item xs={12}>
          <Typography variant={'bodyLarge'}>
            <FormattedMessage {...messages.general} />
          </Typography>
        </Grid>
        <SetupGridItem label={<FormattedMessage {...messages.domuso} />}>
          {editMode ? (
            <Switch
              name="isDomusoActive"
              id="domuso-switch"
              checked={isDomusoActive}
              disabled={!editMode}
              onChange={handleSwitchChange}
              inputProps={{ 'data-checked': isDomusoActive }}
            />
          ) : (
            <OnOffTypography on={isDomusoActive}>
              {isDomusoActive ? (
                <FormattedMessage {...messages.on} />
              ) : (
                <FormattedMessage {...messages.off} />
              )}
            </OnOffTypography>
          )}
        </SetupGridItem>
        <MunibillingSetup
          onChange={onChange}
          orgIntegrations={organizationIntegrations}
          propertyIntegrations={propertyIntegrations}
          editMode={editMode}
        />
        <SetupGridItem
          label={
            <>
              <FormattedMessage {...messages.domusoPropertyCode} />*
            </>
          }
        >
          {editMode ? (
            <TextInput
              name="domusoPropertyCode"
              id="domusoPropertyCode"
              value={removeNonNumericCharacters(domusoPropertyCode)}
              editMode={editMode}
              onChange={onChange}
              disabled={!isDomusoActive}
              required={isDomusoActive}
              maxLength={5}
              minLength={4}
              validateFunction={(value) =>
                value.length === 4 || value.length === 5
              }
              message={messages.domusoCodeLength}
              invalidMessageStyles={{
                float: 'right',
                marginRight: '10%',
              }}
            />
          ) : (
            <Typography>{domusoPropertyCode || '---'}</Typography>
          )}
        </SetupGridItem>
        <ConserviceSetup
          onChange={onChange}
          orgIntegrations={organizationIntegrations}
          propertyIntegrations={propertyIntegrations}
          editMode={editMode}
        />
        {rebaRevMgmt && (
          <SetupGridItem
            label={<FormattedMessage {...messages.revenueManagementProvider} />}
            TooltipProps={{
              title: intl.formatMessage(messages.revenueManagementProviderInfo),
            }}
          >
            <Typography>{revenueManagementProviderDisplayName}</Typography>
          </SetupGridItem>
        )}
        {blueMoonLeaseSigning && (
          <SetupGridItem
            label={<FormattedMessage {...messages.blueMoon} />}
            TooltipProps={{
              title: intl.formatMessage(messages.blueMoonInfo),
            }}
          >
            {!isBlueMoonLoading && blueMoonStatus === 'unknown' && (
              <Typography>---</Typography>
            )}
            {!isBlueMoonLoading && blueMoonStatus !== 'unknown' && (
              <OnOffTypography on={blueMoonStatus === 'on'}>
                {blueMoonStatus === 'on' ? (
                  <FormattedMessage {...messages.on} />
                ) : (
                  <FormattedMessage {...messages.off} />
                )}
              </OnOffTypography>
            )}
          </SetupGridItem>
        )}
        {shouldShowNewScreeningSetup ? (
          <Grid item xs={12}>
            <Divider sx={{ marginY: 2 }} />
            <ScreeningsSetup
              editMode={editMode}
              screeningSetupData={screeningSetupData}
              onChange={onChange}
            />
          </Grid>
        ) : (
          <LegacyScreeningsSetup
            editMode={editMode}
            isTransUnionActive={isTransUnionActive}
            handleSwitchChange={handleSwitchChange}
            intl={intl}
            onChange={onChange}
            transUnionIsLoading={transUnionIsLoading}
            transunionOffLeaseAppOff={transunionOffLeaseAppOff}
            transunionPropertyId={transunionPropertyId}
            transUnionLatestChangeHistoryLog={transUnionLatestChangeHistoryLog}
          />
        )}

        {theWorkNumber &&
          isAffordableOrMixedProperty &&
          doesOrgHaveWorkNumberIntegration && (
            <TheWorkNumberSetup
              onChange={onChange}
              orgIntegrationDetails={orgWorkNumberIntegration}
              propIntegrationDetails={propertyWorkNumberIntegration}
              editMode={editMode}
            />
          )}

        {collectionsOneWay && (
          <CollectionsSetupSection
            {...props}
            onSwitchChange={handleSwitchChange}
            onRadioGroupChange={handleRadioGroupChange}
            editMode={editMode}
          />
        )}
      </Grid>
    </Card>
  );
};

export default IntegrationsSetup;
